import React, { useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toPng } from "html-to-image";
import { decodeShareData, ShareCardType } from "../types";
import BioAgeShareCard from "./cards/BioAgeShareCard";

const SharePage = () => {
  const [searchParams] = useSearchParams();
  const cardRef = useRef<HTMLDivElement>(null);
  const [isSharing, setIsSharing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const data = decodeShareData(searchParams);

  const captureCard = async (): Promise<Blob> => {
    if (!cardRef.current) throw new Error("Card not found");
    const dataUrl = await toPng(cardRef.current, { pixelRatio: 2 });
    const res = await fetch(dataUrl);
    return res.blob();
  };

  const handleShare = async () => {
    setIsSharing(true);
    try {
      const blob = await captureCard();
      const file = new File([blob], "pnoe-result.png", { type: "image/png" });
      await navigator.share({ files: [file] });
    } catch (err: any) {
      if (err?.name !== "AbortError") {
        console.error("Share failed:", err);
      }
    } finally {
      setIsSharing(false);
    }
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      const blob = await captureCard();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "pnoe-result.png";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error("Save failed:", err);
    } finally {
      setIsSaving(false);
    }
  };

  const canShare = (() => {
    try {
      const file = new File(["test"], "test.png", { type: "image/png" });
      return navigator.canShare?.({ files: [file] }) ?? false;
    } catch {
      return false;
    }
  })();

  if (!data) {
    return (
      <div style={styles.page}>
        <div style={styles.errorContainer}>
          <div style={styles.errorTitle}>Invalid share link</div>
          <div style={styles.errorText}>
            This link doesn't contain valid data. Please scan a new QR code from the PNOE app.
          </div>
        </div>
      </div>
    );
  }

  const renderCard = () => {
    switch (data.type) {
      case ShareCardType.BIO_AGE:
        return (
          <BioAgeShareCard
            ref={cardRef}
            bioAge={data.bioAge}
            chronologicalAge={data.chronologicalAge}
            customShare={data.customShare}
          />
        );
      default:
        return null;
    }
  };

  return (
    <div style={styles.page}>
      <div style={styles.cardWrapper}>{renderCard()}</div>
      <div style={styles.buttonRow}>
        {canShare && (
          <button
            onClick={handleShare}
            disabled={isSharing}
            style={styles.shareButton}
            data-testid="share-button"
          >
            {isSharing ? "Sharing..." : "Share"}
          </button>
        )}
        <button
          onClick={handleSave}
          disabled={isSaving}
          style={styles.saveButton}
          data-testid="save-button"
        >
          {isSaving ? "Saving..." : "Save Image"}
        </button>
      </div>
    </div>
  );
};

export default SharePage;

const styles: Record<string, React.CSSProperties> = {
  page: {
    minHeight: "100vh",
    backgroundColor: "#0a0a1a",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    padding: "16px",
    boxSizing: "border-box",
  },
  cardWrapper: {
    width: "100%",
    maxWidth: "405px",
    display: "flex",
    justifyContent: "center",
  },
  buttonRow: {
    display: "flex",
    gap: "12px",
    marginTop: "20px",
    width: "100%",
    maxWidth: "405px",
    justifyContent: "center",
  },
  shareButton: {
    flex: 1,
    padding: "14px 24px",
    borderRadius: "12px",
    border: "none",
    background: "linear-gradient(135deg, #6366f1 0%, #14b8a6 100%)",
    color: "#ffffff",
    fontFamily: "Arial, Helvetica, sans-serif",
    fontSize: "16px",
    fontWeight: 600,
    cursor: "pointer",
  },
  saveButton: {
    flex: 1,
    padding: "14px 24px",
    borderRadius: "12px",
    border: "1px solid rgba(255,255,255,0.2)",
    background: "rgba(255,255,255,0.05)",
    color: "#ffffff",
    fontFamily: "Arial, Helvetica, sans-serif",
    fontSize: "16px",
    fontWeight: 600,
    cursor: "pointer",
  },
  errorContainer: {
    textAlign: "center",
    padding: "32px",
  },
  errorTitle: {
    fontFamily: "Arial, Helvetica, sans-serif",
    fontSize: "24px",
    fontWeight: 700,
    color: "#ffffff",
    marginBottom: "12px",
  },
  errorText: {
    fontFamily: "Arial, Helvetica, sans-serif",
    fontSize: "16px",
    color: "#9ca3af",
    lineHeight: "24px",
  },
};
